import { FormDialog, FormDialogWidgetProps, FormInputs, useMelioIntl } from '@melio/ar-domain';
import { Form, Group, SelectOption, Typography, useMelioForm } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { boolean, number, object, SchemaOf, string } from 'yup';

import { TaxRateSelectWidget } from '../components';
import { AddTaxRateFormFields } from '../types';

const useSchema = () => {
  const { formatMessage } = useMelioIntl();

  const schema = object().shape({
    id: string()
      .required(
        formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.name.validation.required')
      )
      .min(2, formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.name.validation.min')),
    rate: number()
      .nullable()
      .typeError(
        formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.rate.validation.required')
      )
      .moreThan(
        0,
        formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.rate.validation.min')
      )
      .lessThan(
        1,
        formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.rate.validation.max')
      )
      .required(
        formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.rate.validation.required')
      ),
    isDefault: boolean(),
  }) as SchemaOf<AddTaxRateFormFields>;

  return schema;
};

export type AddTaxRateModalScreenProps = FormDialogWidgetProps<
  AddTaxRateFormFields,
  {
    selectOptions: SelectOption[];
    onChangeSelectedTaxRate: (selectedId: string) => void;
    onCreateTaxRate: (name: string) => string;
  }
>;

export const AddTaxRateModalScreen = forwardRef<AddTaxRateModalScreenProps>(
  ({ isSaving, selectOptions, onSubmit, onCreateTaxRate, onChangeSelectedTaxRate, defaultValues, ...props }, ref) => {
    const { registerField, ...useFormResults } = useMelioForm<AddTaxRateFormFields>({
      onSubmit,
      schema: useSchema(),
      defaultValues,
      isSaving,
      subscribeToDefaultValuesChanges: true,
    });

    const { formatMessage } = useMelioIntl();

    return (
      <FormDialog
        header={formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.header.text')}
        primaryButton={{
          label: formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.buttons.confirm.label'),
        }}
        secondaryButton={{
          label: formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.buttons.cancel.label'),
        }}
        useFormResults={useFormResults}
        data-testid="add-tax-rate-modal-screen"
        {...props}
        ref={ref}
      >
        <TaxRateSelectWidget
          labelProps={{
            label: formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.name.label'),
          }}
          placeholder={formatMessage(
            'ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.name.placeholder'
          )}
          colSpan={2}
          onCreateOption={onCreateTaxRate}
          onChangeOption={onChangeSelectedTaxRate}
          selectOptions={selectOptions}
          {...registerField('id')}
        />
        <FormInputs.PercentageField
          labelProps={{
            label: formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.rate.label'),
          }}
          decimalLimit={3}
          placeholder={formatMessage(
            'ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.rate.placeholder'
          )}
          {...registerField('rate')}
        />
        <Group variant="vertical">
          <Typography.Label label="&nbsp;" /> {/* This is a hack to align the checkbox with the text field */}
          <Form.Checkbox
            label={formatMessage('ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.isDefault.label')}
            aria-label={formatMessage(
              'ar.invoiceLifecycle.activities.createInvoice.tax.modal.form.fields.isDefault.label'
            )}
            {...registerField('isDefault')}
          />
        </Group>
      </FormDialog>
    );
  }
);
